import React, { createContext, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatUserName } from '../../../utils/emailTouserName';
import type { AppDispatch } from '../../../store';
import {
  NewConfigHistoryEntry,
  selectConfigConflict,
  selectConfigHistory,
  selectConfigStatus,
  selectDeptMetrics,
  selectPeopleConfigDept,
  selectPeopleConfigLocations,
  selectPeopleConfigOrg,
  selectPeopleConfigOrgDepts,
  selectSaveError,
  setConfigConflict,
  setDeptMetrics as setDeptMetricsAction,
  setSaveError,
  setScope as setScopeAction,
} from '../../../store/slices/peopleConfigSlice';
import { loadPeopleConfig, refreshChangeHistory } from '../../../store/thunks/peopleConfigThunks';
import {
  CardStatus,
  ConfigHistoryEntry,
  ConfigLocation,
  DeptMetricsMap,
  OrgDeptPair,
} from './configTypes';

// ─── Context value type ───────────────────────────────────────────────────────

export interface PeopleConfigContextValue {
  org: string;
  dept: string;
  setScope: (org: string, dept: string) => void;
  orgDepts: OrgDeptPair[];
  locations: ConfigLocation[];
  deptMetrics: DeptMetricsMap;
  setDeptMetrics: (next: DeptMetricsMap) => void;
  status: CardStatus;
  retry: () => void;
  conflict: boolean;
  guard: (save: () => void, onBlocked?: () => void) => void;
  history: ConfigHistoryEntry[];
  /** Call after a successful save; re-reads the audit log the backend wrote for it. */
  log: (entries: NewConfigHistoryEntry[]) => void;
  user: string;
  saveError: string | null;
}

/**
 * Lets a tab place an action (for example the Ramp tab's "Save Configuration") in the page header,
 * next to "Back to Configurations". The setter is stable, so a tab using it does not re-render
 * when the header content changes.
 */
export const HeaderActionContext = createContext<(node: React.ReactNode) => void>(() => {});

/**
 * Read/act access to the shared People Hub Configuration state in Redux (`peopleConfig`).
 *
 * This hook does not fetch anything on mount. It is called by many components at once
 * (tabs, OrgDeptPicker, TabBody), so loading here would fire one consolidated request per caller.
 * The page (`PeopleHubConfiguration/index.tsx`) dispatches `loadPeopleConfig` once; scope changes
 * and Retry dispatch it again from here. Stale responses are discarded inside the thunk.
 */
export const usePeopleConfig = (): PeopleConfigContextValue => {
  const dispatch = useDispatch<AppDispatch>();
  const org = useSelector(selectPeopleConfigOrg);
  const dept = useSelector(selectPeopleConfigDept);
  const orgDepts = useSelector(selectPeopleConfigOrgDepts);
  const locations = useSelector(selectPeopleConfigLocations);
  const deptMetrics = useSelector(selectDeptMetrics);
  const status = useSelector(selectConfigStatus);
  const conflict = useSelector(selectConfigConflict);
  const history = useSelector(selectConfigHistory);
  const saveError = useSelector(selectSaveError);
  const user = formatUserName(useSelector((state: any) => state.auth?.user?.username));

  const setScope = useCallback(
    (o: string, d: string) => {
      dispatch(setScopeAction({ org: o, dept: d }));
      dispatch(loadPeopleConfig({ org: o, dept: d }));
    },
    [dispatch]
  );

  const setDeptMetrics = useCallback(
    (next: DeptMetricsMap) => {
      dispatch(setDeptMetricsAction(next));
    },
    [dispatch]
  );

  const retry = useCallback(() => {
    dispatch(loadPeopleConfig());
  }, [dispatch]);

  const guard = useCallback(
    (save: () => void, onBlocked?: () => void) => {
      // TODO: compare record version with server; set conflict and call onBlocked on mismatch.
      dispatch(setConfigConflict(false));
      dispatch(setSaveError(null));
      save();
    },
    [dispatch]
  );

  const log = useCallback(
    (entries: NewConfigHistoryEntry[]) => {
      if (!entries.length) return;
      dispatch(refreshChangeHistory());
    },
    [dispatch]
  );

  return useMemo<PeopleConfigContextValue>(
    () => ({
      org,
      dept,
      setScope,
      orgDepts,
      locations,
      deptMetrics,
      setDeptMetrics,
      status,
      retry,
      conflict,
      guard,
      history,
      log,
      user,
      saveError,
    }),
    [org, dept, setScope, orgDepts, locations, deptMetrics, setDeptMetrics, status, retry, conflict, guard, history, log, user, saveError]
  );
};
